import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { sourceService } from '../../services/source.service';
import BackgroundService from '../../services/bg.service';

interface DiscoverButtonProps {
  sourceId: string;
  /** Called after a successful discovery (e.g. to refetch the source). */
  onDiscovered?: () => void;
}

/**
 * Standalone "Discover / Refresh schema" button for connectors whose streams
 * are defined by config (e.g. an SFTP source's `tables` JSON) rather than
 * enumerated by a stream selector. Triggers discovery, waits for the job, polls
 * until the catalog is rebuilt, then surfaces the result. Save your config
 * first — discovery samples the files the current config points at.
 */
export function DiscoverButton({ sourceId, onDiscovered }: DiscoverButtonProps) {
  const queryClient = useQueryClient();
  const [discovering, setDiscovering] = useState(false);

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const { job_id } = await sourceService.discoverCatalog(sourceId);
      await BackgroundService.getInstance().waitForJob(job_id);

      let attempts = 0;
      const maxAttempts = 60;
      while (attempts < maxAttempts) {
        await new Promise((r) => setTimeout(r, 5000));
        const result = await sourceService.getCatalog(sourceId);
        if (result.has_catalog && !result.active_discovery_job_id) {
          queryClient.invalidateQueries({ queryKey: ['sourceCatalog', sourceId] });
          const count = result.streams?.length ?? 0;
          toast.success(
            count > 0
              ? `Discovery complete — ${count} stream${count === 1 ? '' : 's'} found`
              : 'Discovery complete — no matching files found',
          );
          onDiscovered?.();
          return;
        }
        attempts++;
      }
      toast.error('Discovery timed out. Please try again.');
    } catch (err) {
      console.error(err);
      toast.error('Catalog discovery failed');
    } finally {
      setDiscovering(false);
    }
  };

  return (
    <Button type="button" variant="outline" size="sm" onClick={handleDiscover} disabled={discovering}>
      <RefreshCw className={`h-4 w-4 mr-2 ${discovering ? 'animate-spin' : ''}`} />
      {discovering ? 'Discovering…' : 'Discover / Refresh schema'}
    </Button>
  );
}
